import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiInfiniteQuery } from '../use-api.js';
import { StatusPill } from '../components/StatusPill.js';
import { ACTIVE_STATUSES } from '../lib/status.js';
import { prLabel, friendlyUser } from '../lib/format.js';
import type { RunsResponse, Run } from '../lib/types.js';

const STATUS_OPTIONS: string[] = [
  'QUEUED',
  'PROVISIONING',
  'RUNNING',
  'SUCCESS',
  'COMPLETED_NO_PR',
  'NEEDS_ITERATION',
  'FAILED',
  'TIMED_OUT',
  'CANCELLED',
];

function buildPath(status: string, user: string, limit: number, next: string | null): string {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (user) params.set('user', user);
  params.set('limit', String(limit));
  if (next) params.set('next', next);
  return `/api/runs?${params.toString()}`;
}

// Distinct users present in the loaded rows, as {id, label} sorted by label.
// Populates the filter dropdown without a server-side user enumeration.
function userOptions(runs: Run[]): { id: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const r of runs) {
    if (r.user_id && !seen.has(r.user_id)) seen.set(r.user_id, friendlyUser(r.user_id));
  }
  return [...seen.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function Runs() {
  const [status, setStatus] = useState('');
  const [user, setUser] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [limit, setLimit] = useState(50);

  const query = useApiInfiniteQuery<RunsResponse>(
    ['runs', status, user, limit],
    (next) => buildPath(status, user, limit, next),
    {
      // Poll every 5s while any loaded run is still in flight; otherwise stop.
      // Active runs are always newest (page 1), so this catches status changes.
      refetchInterval: (q: any) => {
        const pages: RunsResponse[] = q.state.data?.pages ?? [];
        const anyActive = pages.some((p) => p.runs.some((r) => ACTIVE_STATUSES.has(r.status)));
        return anyActive ? 5000 : false;
      },
    },
  );

  const allRuns = query.data?.pages.flatMap((p) => p.runs) ?? [];
  const runs = activeOnly ? allRuns.filter((r) => ACTIVE_STATUSES.has(r.status)) : allRuns;
  const users = userOptions(allRuns);

  return (
    <section>
      <h2>Runs</h2>
      <div className="toolbar">
        <label>
          Status:{' '}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Status filter"
          >
            <option value="">All</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label>
          User:{' '}
          <select value={user} onChange={(e) => setUser(e.target.value)} aria-label="User filter">
            <option value="">All users</option>
            {/* Keep the active selection listable even if a filtered response
                dropped every other user from the loaded rows. */}
            {user && !users.some((u) => u.id === user) && (
              <option value={user}>{friendlyUser(user)}</option>
            )}
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.label}</option>
            ))}
          </select>
        </label>
        <label>
          Limit:{' '}
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} aria-label="Limit">
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
        <label>
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />{' '}
          Active only
        </label>
      </div>

      {query.isLoading && <p>Loading runs…</p>}
      {query.isError && <p className="error">Failed to load runs.</p>}

      {!query.isLoading && !query.isError && (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Run ID</th>
                <th>Repo</th>
                <th>User</th>
                <th>Status</th>
                <th>Created</th>
                <th>Turns</th>
                <th>PR</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && (
                <tr><td colSpan={7} className="empty">No runs.</td></tr>
              )}
              {runs.map((run: Run) => (
                <tr key={run.run_id}>
                  <td><Link to={`/runs/${encodeURIComponent(run.run_id)}`}>{run.run_id}</Link></td>
                  <td>{run.repo ?? '—'}</td>
                  <td title={run.user_id ?? undefined}>{friendlyUser(run.user_id)}</td>
                  <td><StatusPill status={run.status} /></td>
                  <td>{run.created_at ? run.created_at.slice(0, 16) : '—'}</td>
                  <td>
                    {run.num_turns != null && run.max_turns != null
                      ? `${run.num_turns}/${run.max_turns}`
                      : run.num_turns != null
                        ? String(run.num_turns)
                        : '—'}
                  </td>
                  <td>
                    {run.pr_url
                      ? <a href={run.pr_url} target="_blank" rel="noopener noreferrer">{prLabel(run.pr_url)}</a>
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="pager">
            <span className="pager-count">{runs.length} shown</span>
            {query.hasNextPage && (
              <button
                className="btn-secondary"
                onClick={() => query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
              >
                {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
